import { FC } from 'react';
import styled from 'styled-components';
import AchievementBadge from '../AchievementBadge';
import { IAchievementBadge } from '../AchievementBadge/AchievementBadge';
import ProgressBar from '../ProgressBar';
import { IProgressBar } from '../ProgressBar/ProgressBar';
import AchievementProgress from './AchievementProgress';
import type { Award } from '../../services/Gamification';

const Container = styled.div(({ theme }) => {
  const { color } = theme;

  return `
    border-radius: 8px;
    background: ${color.grayscale[900]};
    border: 1px solid ${color.grayscale[400]}40;
    position: relative;
    margin-top: 23px;
    padding: 3px 5px 7px;
  `;
});

const BadgeContainer = styled.div`
  height: 90px;
  top: -23px;
  left: 50%;
  transform: translateX(-50%);
  position: absolute;
`;

const CardHeader = styled.div(({ theme }) => {
  const { color } = theme;

  return `
    background-color: ${color.grayscale[1000]};
    border-radius: 8px;
    padding: 72px 25px 17px;
  `;
});

const CardTitle = styled.h2(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.grayscale[0]};
    text-transform: uppercase;
    font-family: ${type.face};
    font-size: ${type.size.small};
    font-weight: ${type.weight.bold};
    line-height: 1.0625rem;
    text-align: center;
    margin: 0 0 0.1875rem;
  `;
});

const Description = styled.h3(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.grayscale[0]};
    font-family: ${type.face};
    font-size: ${type.size.small};
    font-weight: ${type.weight.regular};
    line-height: 1rem;
    text-align: center;
    margin: 0 0 0.875rem;
  `;
});

const MotivationContainer = styled.div(({ theme }) => {
  const { color, type } = theme;

  return `
    background-color: #449DC8;
    color: ${color.grayscale[1000]};
    padding: 12px 10px;
    font-family: ${type.face};
    font-size: ${type.size.tiny};
    line-height: ${type.size.small};
    border-radius: 8px;
    border-top: 1px solid ${color.grayscale[600]}50;
  `;
});

const ProgressContainer = styled.div`
  padding: 18px 25px;
  margin: 0 5px 5px;
  height: 250px;
  overflow-y: auto;
`;

const SectionTitle = styled.div(({ theme }) => {
  const { type } = theme;
  return `
    font-size: ${type.size.regular};
    font-weight: bold;
    text-align: center;
  `;
});

const RewardText = styled.div(({ theme }) => {
  const { type } = theme;
  return `
    font-size: ${type.size.small};
    text-align: center;
  `;
});

type ProcessedGoalStatus = {
  text: string;
  reached: boolean;
};

export interface IAchievementCard {
  name: string;
  description: string;
  motivationText?: string;
  expiration?: Date;
  progressBar: IProgressBar;
  badge: IAchievementBadge;
  progressStatus: ProcessedGoalStatus[];
  awards: Award[];
}

const AchievementCard: FC<IAchievementCard> = ({
  name,
  description,
  motivationText,
  //expiration,
  progressBar,
  badge,
  progressStatus,
  awards,
}) => {
  const prepareProgress = () => {
    return progressStatus.map((goalProgress) => {
      return { text: goalProgress.text, achieved: goalProgress.reached };
    });
  };

  const printAwards = () => {
    let index = 0;
    return awards.map((award) => {
      index++;
      if (award.award_type === 'points') {
        return <RewardText key={`reward_${index}`}>{award.value} points</RewardText>;
      } else if (award.award_type === 'title') {
        return <RewardText key={`reward_${index}`}>"{award.value}" Title</RewardText>;
      }
    });
  };

  return (
    <Container>
      <BadgeContainer>
        <AchievementBadge {...badge} />
      </BadgeContainer>

      <CardHeader>
        <CardTitle>{name}</CardTitle>
        <Description>{description}</Description>
        <ProgressBar {...progressBar} />
      </CardHeader>

      <ProgressContainer>
        <SectionTitle>Goals</SectionTitle>
        <AchievementProgress
          // progress={[
          //   { text: 'Yay', achieved: true },
          //   { text: 'Yay', achieved: true },
          //   { text: 'Nay', achieved: false },
          // ]}
          progress={prepareProgress()}
        />
      </ProgressContainer>

      {motivationText && <MotivationContainer>{motivationText}</MotivationContainer>}
      <div>
        <SectionTitle>Rewards</SectionTitle>
        {printAwards()}
      </div>

      {/*expiration && <div>expiration date here</div>*/}
    </Container>
  );
};

export default AchievementCard;
